/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';

import { createLocale } from 'locales';

import type { CrumbType } from 'components-new/breadcrumbs/crumb';
import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';

import useGetCertificateOptions from 'state/remote/remote-options/use-get-certificate-options';
import useGetCompetenceOptions from 'state/remote/remote-freelancer/use-get-competence-options';
import type { Option } from 'state/remote/remote-freelancer/use-get-certificate-types';

import { useIntegrationSettingsContext } from 'state/context/organization/OrganisationSettings/IntegrationSettingsContext';

import type {
  RateRule,
  SDBJobsCreate,
  SDBJobsCreateCompetence,
} from 'state/__generated__/tadaahAPI';
import { useApiOrganisationSdbCreateCreate } from 'state/__generated__/tadaahAPI';

import organisationSettingsRoutes, {
  IntegrationSettingsBaseRoute,
} from 'pages/organization/OrganisationSettingsPage/integration-settings/organisation-settings-routes';

import { useOrganisationContext } from 'state/context/OrganisationContext';

import useGetOrganisationRateRules from '../use-get-organisation-rate-rules';
import useGetOrganisationLocations from '../use-get-organisation-locations';

import type { GenericImportedJobType } from './use-organisation-settings-integration-select';

const globalLocalize = createLocale('global');
const localize = createLocale('organisationIntegrationSettingsLocales');
export interface FormValues {
  sdb_job_id: string;
  certificates: string[];
  competence: string;
  location: number;
  max_rate: number;
  max_rate_exempt: number;
  date: string;
  start: string;
  end: string;
  pause: number;
}

export interface UseOrganisationSettingsIntegrationCreateHandler {
  crumbs: CrumbType[];
  selectedJobs: GenericImportedJobType[];
  formOptions: {
    competenceOptions: Option[];
    certificateOptions: Option[];
    locationOptions: Option[];
  };
  form: {
    initialValues: FormValues;
    onSubmit: (values: FormValues) => void;
    isLoading: boolean;
  };
  createdJobIds: string[];
  rateRules: RateRule[] | undefined;
}

const useOrganisationSettingsIntegrationCreate =
  (): UseOrganisationSettingsIntegrationCreateHandler => {
    const navigate = useNavigate();

    const {
      organisations: { selectedOrganisationId },
    } = useOrganisationContext();

    const {
      select: { selectedJobs },
      create: { createdJobIds, setCreatedJobIds },
    } = useIntegrationSettingsContext();

    const { locations } = useGetOrganisationLocations({
      organisationId: (selectedOrganisationId as number).toString(),
    });
    const { competenceOptions: competences } = useGetCompetenceOptions();
    const {
      mutateAsync: createJob,
      data: sdbCreateData,
      error: sdbCreateError,
      isSuccess: sdbCreateSuccess,
      isLoading: sdbCreateIsLoading,
    } = useApiOrganisationSdbCreateCreate();

    const { certificateOptions } = useGetCertificateOptions();

    const { data: rateRulesData } = useGetOrganisationRateRules(
      selectedOrganisationId?.toString() || '',
    );

    useEffect(() => {
      if (sdbCreateError)
        if (
          sdbCreateError?.response?.data &&
          'sdb_job_id' in sdbCreateError?.response?.data
        ) {
          showErrorToast({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            description: (sdbCreateError?.response?.data as any)?.sdb_job_id,
            title: localize('sdbJobAlreadyExistsError'),
          });
        } else {
          showErrorToast({
            description: sdbCreateError.message,
            title: globalLocalize('error'),
          });
        }
    }, [sdbCreateError]);

    useEffect(() => {
      if (sdbCreateSuccess) {
        showSuccessToast({
          description: localize('jobIsCreatedMessage'),
          title: localize('jobCreated'),
        });
      }
    }, [sdbCreateData, sdbCreateSuccess]);

    useEffect(() => {
      if (selectedJobs.length === 0) navigate(IntegrationSettingsBaseRoute);
    }, [navigate, selectedJobs.length]);

    const initialValues = {
      certificates: [],
      competence: '',
      date: '',
      end: '',
      location: 0,
      max_rate: 0,
      max_rate_exempt: 0,
      pause: 0,
      sdb_job_id: '',
      start: '',
    };

    const competenceOptions = useMemo(
      () =>
        competences.map((option) => ({
          label: option.name,
          value: option.code,
        })),
      [competences],
    );

    const locationOptions = useMemo(
      () =>
        locations.map((option) => ({
          label: option.name,
          value: option.id.toString(),
        })),
      [locations],
    );

    const onCrumbClick = useCallback(
      (link: string) => {
        navigate(link);
      },
      [navigate],
    );

    const onCreateJobFormSubmit = useCallback(
      async (values: FormValues) => {
        if (!selectedOrganisationId) throw Error('No Organisation Selected');

        const formData: SDBJobsCreate = {
          ...values,
          certificates: values.certificates.toString(),
          competence: values.competence as SDBJobsCreateCompetence,
          date: dayjs(values.date).format('YYYY-MM-DD'),
          invite_only: false,
          location: Number(values.location),
          max_rate: Number(values.max_rate),
          max_rate_exempt: Number(values.max_rate_exempt),
          organisation: selectedOrganisationId,
          pause: Number(values.pause),
        };
        createJob({
          data: formData,
          organisationId: selectedOrganisationId?.toString(),
        }).then(() => setCreatedJobIds((prev) => [...prev, values.sdb_job_id]));
      },
      [createJob, selectedOrganisationId, setCreatedJobIds],
    );

    const crumbs = [
      {
        link:
          IntegrationSettingsBaseRoute +
          organisationSettingsRoutes.sdbIntegration.path,
        name: localize('sdbIntegration'),
        onClick: () => {
          onCrumbClick(
            IntegrationSettingsBaseRoute +
              organisationSettingsRoutes.sdbIntegration.path,
          );
        },
      },
      {
        link:
          IntegrationSettingsBaseRoute +
          organisationSettingsRoutes.selectJobs.path,
        name: localize('selectJobs'),
        onClick: () => {
          onCrumbClick(
            IntegrationSettingsBaseRoute +
              organisationSettingsRoutes.selectJobs.path,
          );
        },
      },
      {
        name: localize('createJobs'),
      },
    ] as CrumbType[];

    return {
      createdJobIds,
      crumbs,
      form: {
        initialValues,
        isLoading: sdbCreateIsLoading,
        onSubmit: onCreateJobFormSubmit,
      },
      formOptions: {
        certificateOptions,
        competenceOptions,
        locationOptions,
      },
      rateRules: rateRulesData,
      selectedJobs,
    };
  };

export default useOrganisationSettingsIntegrationCreate;
